import React from 'react';
import styled from 'styled-components';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { Typography } from 'src/typography';
import Imgix from 'shared/components/imgix';
import { LockIconSimple } from 'src/assets/lock-icon-simple';
import { mediaQueries } from 'shared/styles';
import { HeadingDescription } from 'src/components/heading-description/heading-description';
import { OutletCountdownTimer } from './outlet-countdown-timer';

type Schedule = {
  endDate?: string;
};

type CollectionsHeaderProps = {
  title: string;
  description?: string;
  image?: string;
  customCategory?: boolean;
  isExclusive?: boolean;
  cardBadgeColor?: string;
  isOutlet?: boolean;
  outletTimerEnabled?: boolean;
  outletTimerMessage?: string;
  schedule?: Schedule;
};

const imgixParams = {
  h: 296,
  w: 1940,
  fit: 'crop',
};

export const CollectionsHeader = ({
  title,
  description,
  image,
  customCategory,
  isExclusive,
  cardBadgeColor,
  isOutlet,
  outletTimerEnabled,
  outletTimerMessage,
  schedule,
}: CollectionsHeaderProps): JSX.Element => {
  const flags = useFlags();
  const isOutletCollectionsEnabled = flags['ecomm.display-outlet-collections-consumer.rollout'] ?? false;

  const hasDescription = !!description;

  // Custom categories only show this header (title/description/image block) when they have an
  // optional description set, matching how a described collection behaves. Without a
  // description, a custom category keeps today's simpler UI (just the back-link heading), which
  // is rendered separately as the page heading -- see collections.tsx.
  const shouldShowHeader = !customCategory || hasDescription || (isOutletCollectionsEnabled && isOutlet);

  // A plain (non-outlet) custom category's `image` field isn't a header/hero image -- it's the
  // category card thumbnail, set as a backwards-compat side effect of the Applications step's
  // card image upload (see apps/admin .../add-custom-category/applications/applications.tsx).
  // Real collections and outlet-type custom categories set `image` from an actual
  // CollectionDetails image upload, so only those should ever render it here.
  const showImage = !!image && (!customCategory || isOutlet);

  const showTimer = isOutletCollectionsEnabled && isOutlet && outletTimerEnabled && schedule?.endDate;

  return (
    <>
      {shouldShowHeader && (
        <Wrapper data-testid='collection-header'>
          <Heading tag='h1' size='medium'>
            {isExclusive && (
              <ExclusiveLabel $color={cardBadgeColor}>
                <LockIconSimple color='currentColor' />
                <span>Exclusive Collection</span>
              </ExclusiveLabel>
            )}
            {title}
          </Heading>

          {description && <HeadingDescription size='large'>{description}</HeadingDescription>}

          {showTimer && !showImage && (
            <TimerOnlyContainer>
              <OutletCountdownTimer endDate={schedule.endDate} message={outletTimerMessage} />
            </TimerOnlyContainer>
          )}

          {showImage && (
            <ImageContainer $hasTimer={!!showTimer}>
              {showTimer && <OutletCountdownTimer endDate={schedule.endDate} message={outletTimerMessage} />}
              <ImageWrapper $hasTimer={!!showTimer}>
                <Image src={image} alt={title} imgixParams={imgixParams} disableSrcSet />
              </ImageWrapper>
            </ImageContainer>
          )}
        </Wrapper>
      )}
    </>
  );
};

const Wrapper = styled.div`
  margin: 0 0 24px;
  width: 100%;
`;

const Heading = styled(Typography.Heading)`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin: 0;

  @media ${mediaQueries.medium} {
    flex-direction: row-reverse;
    justify-content: flex-end;
    align-items: center;
  }
`;

const ExclusiveLabel = styled.div<{ $color?: string }>`
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 16px;
  font-weight: 600;
  color: ${({ $color }) => $color};
  svg {
    width: 20px;
    height: 20px;
  }
`;

const TimerOnlyContainer = styled.div`
  border-radius: var(--dcm-radius-lg);
  margin: 16px 0 0;
  max-width: 970px;
  overflow: hidden;
  width: 100%;
`;

const ImageContainer = styled.div<{ $hasTimer: boolean }>`
  border-radius: var(--dcm-radius-lg);
  margin: 16px 0 0;
  max-width: 970px;
  overflow: hidden;
  width: 100%;
`;

const ImageWrapper = styled.div<{ $hasTimer: boolean }>`
  border-radius: ${({ $hasTimer }) =>
    $hasTimer ? `0 0 var(--dcm-radius-lg) var(--dcm-radius-lg)` : `var(--dcm-radius-lg)`};
  height: 148px;
  overflow: hidden;
  position: relative;
  width: 100%;
`;

const Image = styled(Imgix)`
  display: block;
  height: 100%;
  left: 50%;
  max-width: none;
  position: absolute;
  top: 0;
  transform: translateX(-50%);
`;
